import cuid from "cuid";
import dayjs from "dayjs";
import { v4 } from "uuid";
import { Mappings, MappingsArray, MappingsObject } from "../types/form/integration/mappings";
import { DecryptedAnswerChoice, UnencryptedAnswers } from "../types/form/submission/answer";
import { FormProperties } from "../types/form/form-properties";
import { Score } from "../types/form/submission/score";
import { UnexpectedValueError } from "../unexpected-value-error";
import weekday from "dayjs/plugin/weekday";
import { isRecord } from "../utils/record";
import { has } from "fp-ts/lib/Record";
import * as t from "io-ts";
import { UnreachableVariantError } from "../unreachable-variant-error";
import { roundPrice } from "../utils/money";
import PrettyPrintIoTsErrors from "../io-ts/PrettyPrintIoTsErrors";
import { UnresolvedStaticPath } from "../types/static-path";
import { dayjsFromGermanDate } from "../utils/dayjs";
import { AbbuchungsintervallKurz, printTextAbbuchungsintervallShort } from "../types/form/abbuchungsintervall";
import { isPrintable } from "../utils/printable";

dayjs.extend(weekday);

export type RecursiveValue =
  | number
  | string
  | boolean
  | undefined
  | null
  | RecursiveObject
  | RecursiveValue[];
export type RecursiveObject = { [key in string]: RecursiveValue };

function resolveRecursiveValue(value: RecursiveValue, path: (string | boolean | undefined | ((value: RecursiveValue) => RecursiveValue))[]): RecursiveValue {
  let currentObject = value;
  for (const key of path) {
    if (currentObject === null || currentObject === undefined) {
      return null;
    }
    if (!key) {
      currentObject = null;
    } else if (key === true) {
      continue;
    } else if (typeof key === "function") {
      currentObject = key(currentObject);
    } else if (isRecord(currentObject)) {
      currentObject = currentObject[key];
    } else if (Array.isArray(currentObject)) {
      currentObject = currentObject[parseNumber(key)];
    } else if (typeof currentObject === "string") {
      currentObject = (function (): RecursiveValue {
        switch (key) {
          case "boolean": return !!currentObject;
          case "number": return parseNumber(currentObject);
          case "length": return currentObject.length;
          default: return currentObject;
        }
      })();
    } else if (typeof currentObject === "number") {
      currentObject = (function (): RecursiveValue {
        switch (key) {
          case "boolean": return !!currentObject;
          case "string": return `${currentObject}`;
          default: return currentObject;
        }
      })();
    } else if (typeof currentObject === "boolean") {
      currentObject = (function (): RecursiveValue {
        switch (key) {
          case "boolean": return !!currentObject;
          case "number": return currentObject ? 1 : 0;
          case "string": return `${String(currentObject)}`;
          default: return currentObject;
        }
      })();
    } else {
      throw new UnreachableVariantError(currentObject);
    }
  }
  return currentObject;
}

export type SimpleRecursiveValue =
  | number
  | string
  | boolean
  | undefined
  | null
  | SimpleRecursiveValue[];
export type SimpleRecursiveObject = { [key in string]: SimpleRecursiveValue };

function isSimpleValue(value: RecursiveValue): value is SimpleRecursiveValue {
  return !(typeof value === "object"
    && (!Array.isArray(value) || value.findIndex(isSimpleValue) >= 0))
}

function toJsonIfContainsObject(value: RecursiveValue): SimpleRecursiveValue {
  switch (typeof value) {
    case "string":
    case "number":
    case "bigint":
    case "boolean":
    case "undefined":
      return value;

    default:
      return isSimpleValue(value) ? value : JSON.stringify(value);
  }
}

interface SubmissionProps {
  createdAt: Date | string;
  userLogo: UnresolvedStaticPath | null,
  score: Score;
  formProperties: FormProperties;
}

function resolveSubmissionProp(submission: SubmissionProps, key: string | undefined): unknown | undefined {
  switch (key) {
    case "createdAt":
    case "userLogo":
    case "score":
      return submission[key];
    default: return undefined;
  }
}

interface MappingPropsBase {
  submission: SubmissionProps;
  answers: UnencryptedAnswers;
}

// Hier werden die Mappings für die jeweiligen Verträge, oder ProFit JSON-Daten mit den Werten aus der Submission gematched

type ResolveKnownVariables = {
  KeepWeekPrice?: boolean,
  AnteiligerGrundbeitrag?: boolean,
};

export type ResolveVariables = Record<string, unknown> & ResolveKnownVariables;

export interface ResolveExprProps extends MappingPropsBase {
  expression: string | undefined;
  variables: ResolveVariables;
}

// Hilfesfunktionen um aus string/unknown den richtigen Typ mit Fallback zu bekommen.
function parseStringAsRecord(s: string): Record<string, unknown> {
  const objectFromString = JSON.parse(s);
  switch (typeof objectFromString) {
    case "object":
      if (!objectFromString || Array.isArray(objectFromString)) return {};
      // TODO: Wie kann das object wie ein Record<string, unknown> ohne cast zurückgegeben werden?
      return objectFromString as { [key: string]: unknown };
    default: return {};
  }
}

function parseRecordOrParseStringAsRecord(obj: unknown): Record<string, unknown> {
  switch (typeof obj) {
    case "object":
      return parseRecord(obj);
    case "string": return parseStringAsRecord(obj);
    default: return {};
  }
}

function parseRecord(obj: unknown): Record<string, unknown> {
  return isRecord(obj) ? obj : {};
}

function parseNumber(obj: unknown): number {
  switch (typeof obj) {
    case "number": return obj;
    case "string": return parseFloat(obj);
    default: return 0;
  }
}

const weekdayNameToNumber = function () {
  const mappings: Record<string, number> = {
    "monday": dayjs(new Date("2024-04-15")).locale("de").weekday(),
    "tuesday": dayjs(new Date("2024-04-16")).locale("de").weekday(),
    "wednesday": dayjs(new Date("2024-04-17")).locale("de").weekday(),
    "thursday": dayjs(new Date("2024-04-18")).locale("de").weekday(),
    "friday": dayjs(new Date("2024-04-19")).locale("de").weekday(),
    "saturday": dayjs(new Date("2024-04-20")).locale("de").weekday(),
    "sunday": dayjs(new Date("2024-04-21")).locale("de").weekday(),
  };
  return (secondKey: string) => mappings[secondKey] ?? undefined;
}();

function parseZahlungsweise(value: unknown) {
  switch (value) {
    case "Wöchentlich":
      return "W";
    case "Monatlich":
    default:
      return "M";
  }
}

function parseBoolean(value: unknown) {
  if (typeof value === "string") {
    return value.toUpperCase() === "TRUE"
      || !!parseNumber(value);
  }

  return !!value;
}

const TarifLeistung = t.partial({
  Id: t.Int,
  Preis: t.number,
  Bezeichnung: t.string,
  AbbuchungsintervallKurz: AbbuchungsintervallKurz,
  VerzoegerungMonate: t.Int,
  Score: t.boolean,
  Rhythmus: t.Int,
});
type TarifLeistung = t.TypeOf<typeof TarifLeistung>;

function getLeistungenFromTarif(tarif: Record<string, unknown>, selectedZubuchbar: string[] | undefined, selectedLeistungKind: string): TarifLeistung[] {
  const leistungen: TarifLeistung[] = [];
  if (has(selectedLeistungKind, tarif)) {
    const tarifLeistungOfKind = tarif[selectedLeistungKind];
    if (isRecord(tarifLeistungOfKind)) {
      if (selectedZubuchbar && selectedLeistungKind == "Zubuchbar") {
        for (const selectedZubuchbarItem of selectedZubuchbar) {
          if (typeof selectedZubuchbarItem === "string") {
            const leistungResult = TarifLeistung.decode(tarifLeistungOfKind[selectedZubuchbarItem]);
            if (leistungResult._tag === "Right") {
              leistungen.push(leistungResult.right);
            }
          }
        }
      }
      else {
        for (const leistungRaw of Object.values(tarifLeistungOfKind)) {
          const leistungResult = TarifLeistung.decode(leistungRaw);
          if (leistungResult._tag === "Right") {
            if (selectedLeistungKind === "Inklusive") {
              leistungen.push({ ...leistungResult.right, Preis: 0 });
            } else {
              leistungen.push(leistungResult.right);
            }
          }
        }
      }
    }
  }
  return leistungen;
}

function getAnteiligLeistungenFromTarif(tarif: Record<string, unknown>, selectedZubuchbar: string[] | undefined, selectedLeistungKind: string): TarifLeistung[] {
  const leistungen = getLeistungenFromTarif(tarif, selectedZubuchbar, selectedLeistungKind);
  return selectAnteiligLeistungen(leistungen);
}

interface ResolveSelectedZubuchbarProps extends MappingPropsBase {
  expression: string | undefined;
  variables: Record<string, unknown>;
}

function resolveSelectedZubuchbar({
  expression,
  submission,
  answers,
  variables,
}: ResolveSelectedZubuchbarProps) {
  if (!!expression && typeof expression === "string" && expression !== "*") {
    return expression.split(",").flatMap(el => {
      const resolved = resolveExpr({
        expression: el,
        submission,
        answers,
        variables,
      });
      return typeof resolved === "string" ? [resolved] : [];
    })
  }
  return undefined;
}



const calcLeistungPreis = function (zahlungsweise: "M" | "W", preis: number, abbuchungsintervall: "M" | "T" | "M2" | "Q" | "M4" | "H" | "J"): number {
  return roundPrice((function () {
    switch (zahlungsweise) {
      case "M":
        switch (abbuchungsintervall) {
          case "M":
            return preis;
          case "T":
            return preis * 365 / 7 / 12;
          case "M2":
            return preis / 2;
          case "Q":
            return preis / 3;
          case "M4":
            return preis / 4;
          case "H":
            return preis / 6;
          case "J":
            return preis / 12;
          default:
            throw new UnreachableVariantError(abbuchungsintervall);
        }
      case "W":
        switch (abbuchungsintervall) {
          case "M":
            return preis * 12 * 7 / 365;
          case "T":
            return preis;
          case "M2":
            return preis * 6 * 7 / 365;
          case "Q":
            return preis * 4 * 7 / 365;
          case "M4":
            return preis * 3 * 7 / 365;
          case "H":
            return preis * 2 * 7 / 365;
          case "J":
            return preis * 7 / 365;
          default:
            throw new UnreachableVariantError(abbuchungsintervall);
        }
      default:
        throw new UnreachableVariantError(zahlungsweise);
    }
  })());
};


function calcGesamtbeitrag(grundbeitrag: number, zahlungsweise: "M" | "W", rhythmus: number, anteil: number, selectedVordefiniertList: TarifLeistung[], selectedZubuchbarList: TarifLeistung[],) {
  let value = grundbeitrag * anteil;
  const rhythmusMulti = (zahlungsweise == "W" && rhythmus > 1 ? rhythmus : 1) * anteil;
  for (const vordefiniert of selectedVordefiniertList) {
    if (!vordefiniert.AbbuchungsintervallKurz
      || vordefiniert.AbbuchungsintervallKurz === "E"
      || (vordefiniert.VerzoegerungMonate && vordefiniert.VerzoegerungMonate > 0)
      // Vordefinierte Leistungen sind üblicherweise vom Gesamtpreis ausgeschlossen, können mit true eingeschlossen werden.
      // Service-Gebühr: ausschließen, aber Getränke-Abo möchte man doch noch einschließen
      || !vordefiniert.Score) {
      continue;
    }


    const leistungPreis = calcLeistungPreis(zahlungsweise, vordefiniert.Preis ?? 0, vordefiniert.AbbuchungsintervallKurz);
    value += roundPrice(leistungPreis * rhythmusMulti);
  }
  for (const zubuchbar of selectedZubuchbarList) {
    if (!zubuchbar.AbbuchungsintervallKurz
      || zubuchbar.AbbuchungsintervallKurz === "E"
      // Zubuchbare Leistungen sind üblicherweise im Gesamtpreis enthalten, können aber mit false ausgeschlossen werden.
      || zubuchbar.Score === false) {
      continue;
    }

    const leistungPreis = calcLeistungPreis(zahlungsweise, zubuchbar.Preis ?? 0, zubuchbar.AbbuchungsintervallKurz);
    value += roundPrice(leistungPreis * rhythmusMulti);
  }
  return roundPrice(value);
}

function selectAnteiligLeistungen(leistungen: TarifLeistung[]): TarifLeistung[] {
  return leistungen.filter(leistung =>
    leistung.AbbuchungsintervallKurz == "M"
    || leistung.AbbuchungsintervallKurz == "T");
}

interface ResolvePathValuesProps extends MappingPropsBase {
  valuesArray: string[];
  variables: Record<string, unknown>;
}

function resolvePathValues({
  valuesArray,
  submission,
  answers,
  variables,
}: ResolvePathValuesProps) {
  return valuesArray.map(el => {
    const resolved = resolveExpr({
      expression: el,
      submission,
      answers,
      variables,
    });
    switch (typeof resolved) {
      case "bigint":
      case "number":
        return `${resolved}`;

      case "boolean":
      case "string":
        return resolved;

      case "function":
        return resolved as (value: RecursiveValue) => RecursiveValue;

      case "object":
      case "symbol":
      case "undefined":
        return undefined;
    }
  });
}

export function resolveExpr({
  expression,
  submission,
  answers,
  variables,
}: ResolveExprProps): unknown {
  if (expression === undefined) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-debugger
      debugger;
    }
    return undefined;
  }
  const splitKey = expression.split(":");
  const keyType: string | undefined = splitKey[0];
  const firstKey: string | undefined = splitKey[1];
  const secondKey: string | undefined = splitKey[2];
  const valueType: string | undefined = splitKey[3];
  const valueConversion: string | undefined = splitKey[4];
  let finalValue: unknown;
  if (keyType === "array") {
    if (firstKey === "fromObject") {
      const allValues = [...splitKey.slice(2)].join(":");
      const splitValues = allValues.split("&");
      const valuesArray = splitValues[1]?.split(",") ?? [];
      const obj = resolveExpr({
        expression: splitValues[0],
        submission,
        answers,
        variables,
      });

      const parsedObj = parseRecordOrParseStringAsRecord(obj);
      const collectedValues: unknown[] = [];
      if (parsedObj && typeof parsedObj === "object" && !Array.isArray(parsedObj)) {
        const values = valuesArray.map((el) => {
          return resolveExpr({
            expression: el,
            submission,
            answers,
            variables,
          });
        });

        values.forEach((el: unknown) => {
          if (el && typeof el === "string" && parsedObj[el]) {
            collectedValues.push(parsedObj[el]);
          }
        });

        finalValue = collectedValues;
      }
    } else if (firstKey === "map") {
      const allValues = [...splitKey.slice(2)].join(":");
      const splitValues = allValues.split("&");

      const obj = resolveExpr({
        expression: splitValues[0],
        submission,
        answers,
        variables,
      });
      const values = resolvePathValues({
        valuesArray: splitValues[1]?.split(',') ?? [],
        submission,
        answers,
        variables,
      });
      if (Array.isArray(obj)) {
        finalValue = obj.map(item => (
          resolveRecursiveValue(item as RecursiveValue, values)));
      }
    }
  } else if (keyType === "fromObject") {
    const allValues = [...splitKey.slice(1)].join(":");
    const splitValues = allValues.split("&");

    const obj = resolveExpr({
      expression: splitValues[0],
      submission,
      answers,
      variables,
    });
    const parsedObj = parseRecordOrParseStringAsRecord(obj) as RecursiveObject;
    const values = resolvePathValues({
      valuesArray: splitValues[1]?.split(",") ?? [],
      submission,
      answers,
      variables,
    });
    finalValue = toJsonIfContainsObject(
      resolveRecursiveValue(parsedObj, values));
  } else if (keyType === "page") {
    if (firstKey && secondKey) {
      const answer = answers[firstKey];
      const answerData = answer?.data;
      if (answerData) {
        let fieldData: DecryptedAnswerChoice | undefined = undefined;
        if (secondKey.includes("-")) {
          const splitComponent = secondKey.split("-");
          let combined: string | undefined = undefined;
          splitComponent.forEach((el) => {
            const value = answerData[el]?.value;
            if (isPrintable(value)) {
              if (combined) {
                combined = `${combined} ${value ?? ""}`;
              } else {
                combined = `${value}`;
              }
            }
          });
          finalValue = combined;
        } else if (secondKey === "first") {
          for (const key of Object.keys(answerData)) {
            fieldData = answerData[key];
          }
        } else {
          fieldData = answerData[secondKey];
        }
        if (fieldData) {
          if (valueType === "boolean") {
            finalValue = !!fieldData.value;
          } else if (valueType === "value") {
            if (valueConversion === "number") {
              finalValue = isPrintable(fieldData.value) ? parseInt(`${fieldData.value}`) : 0;
            } else if (valueConversion === "german-date") {
              const date = new Date(isPrintable(fieldData.value) ? fieldData.value.toString() : "");
              finalValue = date ? dayjs(date).format("DD.MM.YYYY") : fieldData.value;
            } else {
              finalValue = fieldData.value;
            }
          } else if (valueType === "label") {
            if (valueConversion === "number") {
              finalValue = parseInt(`${fieldData.label}`);
            } else if (valueConversion === "boolean") {
              finalValue = !!fieldData.label;
            } else {
              finalValue = fieldData.label;
            }
          } else {
            finalValue = fieldData.value;
          }

          if (valueConversion?.startsWith("when")) {
            // TODO: Das hier ist noch nicht getestet worden!
            const whenExpr = splitKey.slice(5).join(":");
            const whenValue = resolveExpr({
              expression: whenExpr,
              submission,
              answers,
              variables,
            });
            if (whenValue) {
              finalValue = fieldData.label
            }
          }
        }
      }
    }
  } else if (keyType === "formProperties") {
    const allKeys = splitKey.slice(1);
    const formPropertyValue = resolveRecursiveValue(submission.formProperties, allKeys);
    finalValue = toJsonIfContainsObject(formPropertyValue);
  } else if (keyType === "date") {
    const contractCreationDate = submission.createdAt;
    if (firstKey === "today") {
      finalValue = dayjs(contractCreationDate)
        .locale("de")
        .format("DD.MM.YYYY");
    } else if (firstKey === "next-month") {
      finalValue = dayjs(contractCreationDate)
        .add(1, "month")
        .set("date", 1)
        .locale("de")
        .format("DD.MM.YYYY");
    } else if (firstKey === "next-monthday") {
      const start = dayjs(contractCreationDate);
      const monthday = parseNumber(secondKey);
      const startDate = start.date();
      const selectedMonthDate =
        startDate <= monthday ? start
          : start.add(1, "month");
      finalValue = selectedMonthDate
        .set("date", parseNumber(secondKey))
        .locale("de")
        .format("DD.MM.YYYY");
    } else if (firstKey === "next-monday") {
      finalValue = dayjs(contractCreationDate)
        .locale("de")
        .weekday(7)
        .format("DD.MM.YYYY");
    }
    else if (firstKey === "next-weekday") {
      let targetWeekday: number;
      if (secondKey) {
        targetWeekday = parseInt(secondKey);
        if (isNaN(targetWeekday)) {
          targetWeekday =
            weekdayNameToNumber(secondKey)
            ?? (function () {
              const resolvedValue = resolveExpr({
                expression: secondKey,
                answers,
                submission,
                variables,
              });
              return isPrintable(resolvedValue) ? parseInt(`${resolvedValue}`) : undefined;
            })()
            ?? 0;
        }
      }
      else
        targetWeekday = 0;

      const currentValue = dayjs(contractCreationDate).locale("de");
      finalValue = currentValue
        .weekday(currentValue.weekday() < targetWeekday ? targetWeekday : targetWeekday + 7)
        .format("DD.MM.YYYY");
    }
  } else if (keyType === "math") {
    const allValues = [...splitKey.slice(2)].join(":");
    const singleValues = allValues.split("&");
    const singleMappedValues: number[] = singleValues.map((el, index) => {
      const subEl = resolveSubmissionProp(submission, el);
      if (subEl && typeof subEl === "string") {
        return parseFloat(subEl);
      } else if (!isNaN(parseFloat(el))) {
        return parseFloat(el);
      } else if (el === "rest-days-of-month") {
        // provisional rest days of month
        const daysInMonth = dayjs(submission.createdAt).daysInMonth();
        const currentDay = dayjs(submission.createdAt).date();
        const daysLeft = daysInMonth - currentDay + 1;
        return daysInMonth / daysLeft;
      } else if (el === "rest-days-until-monday") {
        // provisional rest days of week
        const daysInWeek = 7;
        const currentWeekday = dayjs(submission.createdAt).locale("de").weekday();
        const daysLeft = daysInWeek - currentWeekday;
        return daysInWeek / daysLeft;
      } else if (el.startsWith("math")) {
        const joinedValues = [...singleValues.slice(index)].join("&");
        return parseNumber(resolveExpr({
          submission,
          answers,
          expression: joinedValues,
          variables,
        }));
      } else {
        return parseNumber(resolveExpr({
          submission,
          answers,
          expression: el,
          variables,
        }));
      }
    });
    const value = (function () {
      if (firstKey === "min") {
        return Math.min(...singleMappedValues);
      } else if (firstKey === "max") {
        return Math.max(...singleMappedValues);
      } else {
        let value = 0;
        singleMappedValues.forEach((el, index) => {
          if (el) {
            if (index === 0) {
              value = el;
            } else if (firstKey === "add") {
              value = value as number + el;
            } else if (firstKey === "divide") {
              value = value / el;
            }
          }
        });
        return value;
      }
    })();
    finalValue = typeof value === "number" ? value
      : parseFloat(parseFloat(value).toFixed(2));
  } else if (keyType === "string") {
    finalValue = [...splitKey.slice(1)].join(":");
  } else if (keyType === "float") {
    finalValue = firstKey ? parseFloat(parseFloat(firstKey).toFixed(2)) : 0;
  } else if (keyType === "int") {
    finalValue = firstKey ? parseInt(firstKey) : 0;
  } else if (keyType === "bool") {
    finalValue = firstKey ? parseBoolean(firstKey) : false;
  } else if (keyType === "id") {
    if (firstKey === "uuid") {
      finalValue = v4();
    } else {
      finalValue = cuid();
    }
  } else if (keyType === "toMoney") {
    const expr = splitKey.slice(2).join(":");
    const evaluatedValueNumber = (function (): number {
      const evaluatedValue = resolveExpr({
        submission,
        answers,
        expression: expr,
        variables,
      });
      if (typeof evaluatedValue === "string") {
        return parseFloat(evaluatedValue) ?? 0;
      } else if (typeof evaluatedValue !== "number") {
        return 0;
      }
      return evaluatedValue;
    })();

    if (firstKey === "comma") {
      finalValue = evaluatedValueNumber.toFixed(2).replace(".", ",");
    } else if (firstKey === "point") {
      finalValue = evaluatedValueNumber.toFixed(2)
    }
  } else if (keyType === "get") {
    // get:VARIABLENNAME
    finalValue = firstKey ? variables[firstKey] : undefined;
  } else if (keyType === "set") {
    // set:VARIABLENNAME:VARIABLENWERT|FORTSETZUNG
    const exprCont = splitKey.slice(2).join(":").split('|');
    const exprValue = resolveExpr({
      expression: exprCont[0],
      submission,
      answers,
      variables,
    });
    if (firstKey) {
      variables[firstKey] = exprValue;
    }
    finalValue = resolveExpr({
      expression: exprCont.slice(1).join('|'),
      submission,
      answers,
      variables,
    });
  } else if (keyType === "contract") {
    if (firstKey === "is-debit-monthday-visible") {
      const allValues = [...splitKey.slice(2)].join(":");
      const splitValues = allValues.split("&").map(expression =>
        resolveExpr({
          expression,
          submission,
          answers,
          variables,
        }));
      const tarife = parseRecordOrParseStringAsRecord(splitValues[0]);
      const selectedTarifLabel = splitValues[1];
      finalValue = false;
      if (typeof selectedTarifLabel === "string" && isRecord(tarife)) {
        const tarif = tarife[selectedTarifLabel];
        if (isRecord(tarif)) {
          const dueDate = tarif["ErsteFaelligkeit"];
          finalValue = dueDate === "debitMonthday";
        }
      }
    } else if (firstKey === "get-validate-min-age") {
      const allValues = [...splitKey.slice(2)].join(":");
      const splitValues = allValues.split("&").map(expression =>
        resolveExpr({
          expression,
          submission,
          answers,
          variables,
        }));
      const tarife = parseRecordOrParseStringAsRecord(splitValues[0]);
      const selectedTarifLabel = splitValues[1];
      finalValue = false;
      if (typeof selectedTarifLabel === "string" && isRecord(tarife)) {
        const tarif = tarife[selectedTarifLabel];
        if (isRecord(tarif)) {
          const u18PruefungDeaktivieren = tarif["U18PruefungDeaktivieren"];
          const abschlussImStudio = tarif["AbschlussImStudio"];
          finalValue = u18PruefungDeaktivieren || abschlussImStudio ? 0 : 18;
        }
      }
    } else if (firstKey === "get-under-18-warning") {
      const allValues = [...splitKey.slice(2)].join(":");
      const splitValues = allValues.split("&").map(expression =>
        resolveExpr({
          expression,
          submission,
          answers,
          variables,
        }));
      const tarife = parseRecordOrParseStringAsRecord(splitValues[0]);
      const selectedTarifLabel = splitValues[1];
      finalValue = false;
      if (typeof selectedTarifLabel === "string" && isRecord(tarife)) {
        const tarif = tarife[selectedTarifLabel];
        if (isRecord(tarif)) {
          const abschlussImStudio = tarif["AbschlussImStudio"];
          const u18PruefungDeaktivieren = tarif["U18PruefungDeaktivieren"];
          finalValue = abschlussImStudio && !u18PruefungDeaktivieren;
        }
      }
    } else if (firstKey === "start-date") {
      const allValues = [...splitKey.slice(2)].join(":");
      const splitValues = allValues.split("&").map(expression =>
        resolveExpr({
          expression,
          submission,
          answers,
          variables,
        }));
      const tarife = parseRecordOrParseStringAsRecord(splitValues[0]);
      const selectedTarifLabel = splitValues[1];
      if (typeof selectedTarifLabel === "string" && isRecord(tarife)) {
        const tarif = tarife[selectedTarifLabel];
        if (isRecord(tarif)) {
          const dueDate = tarif["ErsteFaelligkeit"];
          const kostenfreierZeitraumTage = (function (value) {
            return typeof value === "number" ? value : 0;
          })(tarif["KostenfreieTestphase"]);
          if (dueDate === "creationDate" && kostenfreierZeitraumTage !== 0) {
            throw new UnexpectedValueError(kostenfreierZeitraumTage,
              "Der kostenfreie Zeitraum ist für Vertragsstart = Abschlussdatum nicht erlaubt.");
          }

          const abschlussdatumPlusKostenfreierZeitraum =
            dayjs(submission.createdAt).locale("de")
              .add(kostenfreierZeitraumTage, "day");

          finalValue = abschlussdatumPlusKostenfreierZeitraum.format("DD.MM.YYYY");
        }
      }
    } else if (firstKey === "testphase") {
      const allValues = [...splitKey.slice(2)].join(":");
      const end = resolveExpr({
        expression: `contract:start-date:${allValues}`,
        submission,
        answers,
        variables,
      });
      if (end) {
        const start = dayjs(submission.createdAt).locale("de").format("DD.MM.YYYY");
        if (start !== end) {
          const endMinusOne =
            dayjsFromGermanDate(end)
              ?.locale("de")
              .add(-1, "day")
              .format("DD.MM.YYYY");
          return { start: start, end: endMinusOne };
        }
      }
    } else if (firstKey === "first-full-debit-date") {
      const allValues = [...splitKey.slice(2)].join(":");
      const splitValuesRaw = allValues.split("&");
      const splitValues = splitValuesRaw.map(expression =>
        resolveExpr({
          expression,
          submission,
          answers,
          variables,
        }));
      const tarife = parseRecordOrParseStringAsRecord(splitValues[0]);
      const selectedTarifLabel = splitValues[1];
      if (typeof selectedTarifLabel === "string" && isRecord(tarife)) {
        const tarif = tarife[selectedTarifLabel];
        if (isRecord(tarif)) {
          const dueDate = tarif["ErsteFaelligkeit"];
          const abschlussdatumPlusKostenfreierZeitraumStr =
            resolveExpr({
              expression: `contract:start-date:${splitValuesRaw[0] ?? ""}&${splitValuesRaw[1] ?? ""}`,
              submission,
              answers,
              variables,
            });
          const abschlussdatumPlusKostenfreierZeitraum =
            dayjsFromGermanDate(abschlussdatumPlusKostenfreierZeitraumStr)
              ?.locale("de")
              .toDate();

          if (abschlussdatumPlusKostenfreierZeitraum) {
            switch (dueDate) {
              case "monday":
              case "tuesday":
              case "wednesday":
              case "thursday":
              case "friday":
                finalValue = resolveExpr({
                  expression: `date:next-weekday:${dueDate}`,
                  submission: { ...submission, createdAt: abschlussdatumPlusKostenfreierZeitraum },
                  answers,
                  variables,
                });
                break;
              case "firstDayOfMonth":
                {
                  finalValue = resolveExpr({
                    expression: `date:next-monthday:1`,
                    submission: { ...submission, createdAt: abschlussdatumPlusKostenfreierZeitraum },
                    answers,
                    variables,
                  });
                }
                break;
              case "debitMonthday":
                {
                  const debitMonthday = parseNumber(splitValues[2]);
                  finalValue = resolveExpr({
                    expression: `date:next-monthday:${debitMonthday}`,
                    submission: { ...submission, createdAt: abschlussdatumPlusKostenfreierZeitraum },
                    answers,
                    variables,
                  });
                }
                break;
              case "creationDate":
                // Für kostenfreien Zeitraum wurde es bereits in start-date geprüft.
                finalValue = resolveExpr({
                  expression: `date:today`,
                  submission,
                  answers,
                  variables,
                });
                break;

              default:
                throw new UnexpectedValueError(dueDate, "Unbekannter Vertragsstart");
            }
          }
        }
      }
    } else if (firstKey === "keep-week-price") {
      // Prüft anhand der Tarifkonfiguration, ob die Preise als Wochenpreis gelassen werden.
      // Das ist für den XML-Export.
      const allValues = [...splitKey.slice(2)].join(":");
      const splitValues = allValues.split("&");
      const tarife = parseRecordOrParseStringAsRecord(resolveExpr({
        expression: splitValues[0],
        submission,
        answers,
        variables,
      }));
      const selectedTarifLabel = resolveExpr({
        expression: splitValues[1],
        submission,
        answers,
        variables,
      });

      let keepWeekPrice = false;
      if (typeof selectedTarifLabel === "string" && isRecord(tarife)) {
        const tarif = tarife[selectedTarifLabel];
        if (isRecord(tarif)) {
          const zahlungsweise = parseZahlungsweise(tarif["Zahlungsweise"]);
          const rhythmus = parseNumber(tarif["Rhythmus"]);
          const wochenpreiseBewerben = !!tarif["WochenpreiseBewerben"];
          keepWeekPrice = zahlungsweise === "W" && rhythmus > 1 && wochenpreiseBewerben;
        }
      }
      finalValue = keepWeekPrice;
    } else if (firstKey === "leistungen") {
      const allValues = [...splitKey.slice(2)].join(":");
      const splitValues = allValues.split("&");
      const tarife = parseRecordOrParseStringAsRecord(resolveExpr({
        expression: splitValues[0],
        submission,
        answers,
        variables,
      }));
      const selectedTarifLabel = resolveExpr({
        expression: splitValues[1],
        submission,
        answers,
        variables,
      });
      const selectedLeistungKinds = (function (allValues) {
        if (!!allValues && typeof allValues === "string" && allValues !== "*") {
          return allValues.split(",");
        }
        return ["Inklusive", "Vordefiniert", "Zubuchbar"];
      })(splitValues[2]);
      const selectedZubuchbar = resolveSelectedZubuchbar({
        expression: splitValues[3],
        submission,
        answers,
        variables,
      });

      const leistungen: TarifLeistung[] = [];
      if (typeof selectedTarifLabel === "string" && isRecord(tarife)) {
        const tarif = tarife[selectedTarifLabel];
        if (isRecord(tarif)) {
          for (const selectedLeistungKind of selectedLeistungKinds) {
            leistungen.push(...getLeistungenFromTarif(tarif, selectedZubuchbar, selectedLeistungKind));
          }
        }
      }
      for (let i = 0; i < leistungen.length; i++) {
        const leistung = leistungen[i];
        if (!leistung) continue;
        const rhythmus = leistung.Rhythmus ?? 1;
        if (leistung.AbbuchungsintervallKurz === "T"
          && rhythmus > 1
          && leistung.Preis
          && !variables.KeepWeekPrice) {
          leistungen[i] = { ...leistung, Preis: leistung.Preis * rhythmus };
        }
      }
      finalValue = leistungen;
    } else if (firstKey === "grundbeitrag-periodisch") {
      const allValues = [...splitKey.slice(2)].join(":");
      const splitValues = allValues.split("&");
      const tarife = parseRecordOrParseStringAsRecord(resolveExpr({
        expression: splitValues[0],
        submission,
        answers,
        variables,
      }));
      const selectedTarifLabel = resolveExpr({
        expression: splitValues[1],
        submission,
        answers,
        variables,
      });
      let value = 0;
      if (typeof selectedTarifLabel === "string" && isRecord(tarife)) {
        const tarif = tarife[selectedTarifLabel];
        if (isRecord(tarif)) {
          const grundbeitrag = parseNumber(tarif["Grundbeitrag"]);
          const zahlungsweise = parseZahlungsweise(tarif["Zahlungsweise"]);
          const wochenpreiseBewerben = !!tarif["WochenpreiseBewerben"];
          const rhythmus = wochenpreiseBewerben || variables.KeepWeekPrice ? 1
            : Math.max(1, parseNumber(tarif["Rhythmus"]));

          value = grundbeitrag;
          if (zahlungsweise === "W" && rhythmus > 1) {
            value *= rhythmus;
          }
        }
      }
      finalValue = roundPrice(value);
    } else if (firstKey === "beitrag-periodisch") {
      const allValues = [...splitKey.slice(2)].join(":");
      const splitValues = allValues.split("&");
      const tarife = parseRecordOrParseStringAsRecord(resolveExpr({
        expression: splitValues[0],
        submission,
        answers,
        variables,
      }));
      const selectedTarifLabel = resolveExpr({
        expression: splitValues[1],
        submission,
        answers,
        variables,
      });
      const selectedZubuchbarLabels = resolveSelectedZubuchbar({
        expression: splitValues[2],
        submission,
        answers,
        variables,
      });
      let value = 0;
      if (typeof selectedTarifLabel === "string" && isRecord(tarife)) {
        const tarif = tarife[selectedTarifLabel];
        if (isRecord(tarif)) {
          const grundbeitrag = parseNumber(tarif["Grundbeitrag"]);
          const zahlungsweise = parseZahlungsweise(tarif["Zahlungsweise"]);
          const wochenpreiseBewerben = !!tarif["WochenpreiseBewerben"];
          const rhythmus = wochenpreiseBewerben || variables.KeepWeekPrice ? 1
            : Math.max(1, parseNumber(tarif["Rhythmus"]));
          const selectedVordefiniertList = getLeistungenFromTarif(tarif, undefined, "Vordefiniert");
          const selectedZubuchbarList = getLeistungenFromTarif(tarif, selectedZubuchbarLabels, "Zubuchbar");

          value = calcGesamtbeitrag(grundbeitrag, zahlungsweise, rhythmus, 1, selectedVordefiniertList, selectedZubuchbarList);
        }
      }
      finalValue = roundPrice(value);
    } else if (firstKey === "beitrag-anteilig") {
      const allValues = [...splitKey.slice(2)].join(":");
      const splitValues = allValues.split("&");
      const tarife = parseRecordOrParseStringAsRecord(resolveExpr({
        expression: splitValues[0],
        submission,
        answers,
        variables,
      }));
      const selectedTarifLabel = resolveExpr({
        expression: splitValues[1],
        submission,
        answers,
        variables,
      });
      const selectedDebitMonthDay = splitValues.length === 4 ? splitValues[2] : undefined;
      const selectedZubuchbarLabelsExpression = splitValues.length === 4 ? splitValues[3] : splitValues[2];
      const selectedZubuchbarLabels = resolveSelectedZubuchbar({
        expression: selectedZubuchbarLabelsExpression,
        submission,
        answers,
        variables,
      });
      let value = 0;
      if (typeof selectedTarifLabel === "string" && isRecord(tarife)) {
        const tarif = tarife[selectedTarifLabel];
        if (isRecord(tarif)) {
          const grundbeitrag = parseNumber(tarif["Grundbeitrag"]);
          const zahlungsweise = parseZahlungsweise(tarif["Zahlungsweise"]);
          const rhythmus = variables.KeepWeekPrice ? 1
            : parseNumber(tarif["Rhythmus"]);

          const beginn = dayjsFromGermanDate(
            resolveExpr({
              expression: `contract:start-date:${splitValues[0] ?? ""}&${splitValues[1] ?? ""}&${selectedDebitMonthDay ?? ""}`,
              submission,
              answers,
              variables,
            }));
          const firstFullDate = dayjsFromGermanDate(resolveExpr({
            expression: `contract:first-full-debit-date:${splitValues[0] ?? ""}&${splitValues[1] ?? ""}&${selectedDebitMonthDay ?? ""}`,
            submission,
            answers,
            variables,
          }));
          if (beginn && firstFullDate) {
            // firstFullDate und beginn => zwischen tage
            const tage = firstFullDate.diff(beginn, "days");
            if (tage < 0) {
              // Hier sollten die Tage niemals negativ sein, da firstFullDate ist immer
              // am gleichen Tag oder in Zukunft von beginn.
              throw new UnexpectedValueError(tage, "Anteilige Tage sind negativ");
            }

            let anteil: number;
            switch (zahlungsweise) {
              case "M":
                anteil = 12 * tage / 365;
                break;

              case "W":
                anteil = tage / 7;
                break;

              default:
                throw new UnreachableVariantError(zahlungsweise);
            }

            // Entwicklungsauftrag #2192
            // ProFit Smart erwartet im XML den anteiligen Beitrag nur auf Basis vom Grundbeitrag,
            // da es selbst den anteiligen Wert für die Leistungen berechnet. Für das XML dürfen daher
            // keine Leistungen für die Beitragsberechnung verwendet werden. Im Prinzip muss einfach nur
            // eine leere Liste für Vordefiniert und Zubuchbar der calcGesamtbeitrag-Funktion übergeben
            // werden. Dazu wird die Variable "set:AnteiligerGrundbeitrag:bool:true" global in der
            // XML-Erstellung gesetzt. Mit diesem Flag erreichen wir also nur eine Änderung beim XML, während
            // im PDF weiterhin der korrekte anteilige Gesamtbeitrag berechnet wird.
            // XML: Anteiliger Grundbeitrag = ohne anteiligen Leistungen
            // PDF: Anteiliger Gesamtbeitrag = mit anteiligen Leistungen
            const selectedAnteiligVordefiniertList = variables.AnteiligerGrundbeitrag ? []
              : getAnteiligLeistungenFromTarif(tarif, undefined, "Vordefiniert");
            const selectedAnteiligZubuchbarList = variables.AnteiligerGrundbeitrag ? []
              : getAnteiligLeistungenFromTarif(tarif, selectedZubuchbarLabels, "Zubuchbar");

            // Entwicklungsauftrag #1801
            // Zuvor wurde der anteilige Beitrag auf den Gesamtbeitrag berechnet.
            // Mit diesem Entwicklungsauftrag soll das geändert werden. Die vorauszahlenden Leistungen 
            // sollen nicht mehr anteilig berechnet werden. Das bedeutet, dass nur noch wöchentliche
            // monatliche Leistungen für die Berechnung verwendet werden. Somit fliegen folgende Leistungen
            // heraus: M2, Q, M4, H, J
            value = calcGesamtbeitrag(grundbeitrag, zahlungsweise, rhythmus, anteil,
              selectedAnteiligVordefiniertList,
              selectedAnteiligZubuchbarList);
          }
        }
      }
      finalValue = roundPrice(value);
    } else if (firstKey === "beitrag-intervall") {
      const allValues = [...splitKey.slice(2)].join(":");
      const splitValues = allValues.split("&");
      const tarife = parseRecordOrParseStringAsRecord(resolveExpr({
        expression: splitValues[0],
        submission,
        answers,
        variables,
      }));
      const selectedTarifLabel = resolveExpr({
        expression: splitValues[1],
        submission,
        answers,
        variables,
      });
      let zahlungsweise = 0;
      if (typeof selectedTarifLabel === "string" && isRecord(tarife)) {
        const tarif = tarife[selectedTarifLabel];
        if (isRecord(tarif)) {
          zahlungsweise = parseZahlungsweise(tarif["Zahlungsweise"]) == "M" ? 1 : 0;
        }
      }
      finalValue = roundPrice(zahlungsweise);

    } else if (firstKey === "abbuchung-intervall") {
      const allValues = [...splitKey.slice(2)].join(":");
      const splitValues = allValues.split("&");
      const tarife = parseRecordOrParseStringAsRecord(resolveExpr({
        expression: splitValues[0],
        submission,
        answers,
        variables,
      }));
      const selectedTarifLabel = resolveExpr({
        expression: splitValues[1],
        submission,
        answers,
        variables,
      });
      const valueConversion = splitValues[2];
      let abbuchungsintervall: number | string = 0;
      if (typeof selectedTarifLabel === "string" && isRecord(tarife)) {
        const tarif = tarife[selectedTarifLabel];
        if (isRecord(tarif)) {
          abbuchungsintervall = parseNumber(tarif["Rhythmus"] ?? tarif["Abbuchungsintervall"]);
          if (valueConversion === "text") {
            switch (abbuchungsintervall) {
              case 1:
                abbuchungsintervall = "7-tägig";
                break;
              case 2:
                abbuchungsintervall = "14-tägig";
                break;
              case 4:
                abbuchungsintervall = "28-tägig";
                break;
              case 0:
              default:
                abbuchungsintervall = "monatlich";
                break;
            }
          }
        }
      }
      finalValue = abbuchungsintervall;
    } else if (firstKey === "mapper") {
      if (secondKey === "leistung-mit-preis") {
        finalValue = (obj?: unknown) => {
          const decodeResult = TarifLeistung.decode(obj);
          if (decodeResult._tag === "Left") {
            return PrettyPrintIoTsErrors(decodeResult.left);
          }

          const leistung = decodeResult.right;
          const note = !leistung.AbbuchungsintervallKurz ? ""
            : printTextAbbuchungsintervallShort(
              leistung.AbbuchungsintervallKurz,
              variables.KeepWeekPrice ? 1
                : leistung.Rhythmus ?? 1);
          return `${leistung?.Bezeichnung ?? ""} ${(leistung?.Preis ?? 0).toFixed(2).replace(".", ",")}€${note}`
        };
      } else if (secondKey === "format-leistung-xml") {
        finalValue = (obj: unknown) => {
          const decodeResult = TarifLeistung.decode(obj);
          if (decodeResult._tag === "Left") {
            return PrettyPrintIoTsErrors(decodeResult.left);
          }

          return {
            ...decodeResult.right,
            Preis: decodeResult.right.Preis?.toFixed(2).replace(".", ",")
          };
        };
      }
    } else {
      finalValue = undefined;
    }
  }
  else {
    finalValue = resolveSubmissionProp(submission, keyType);
  }

  return finalValue;
}


export interface ResolveMappingsArrayProps extends MappingPropsBase {
  mappings: MappingsArray;
  variables: ResolveVariables;
}

function resolveMappingsArray({
  mappings,
  submission,
  answers,
  variables,
}: ResolveMappingsArrayProps): unknown[] {
  return mappings.map(value => resolveMappings({
    mappings: value,
    submission,
    answers,
    variables,
  }));
}


export interface ResolveMappingsObjectProps extends MappingPropsBase {
  mappings: MappingsObject;
  variables: ResolveVariables;
}

function resolveMappingsObject({
  mappings,
  submission,
  answers,
  variables,
}: ResolveMappingsObjectProps) {
  const values: { [key: string]: unknown } = {};
  for (const key in mappings) {
    const value = mappings[key] as typeof mappings[typeof key];
    values[key] = resolveMappings({
      mappings: value,
      submission,
      answers,
      variables,
    });
  }
  return values;
}


export interface ResolveMappingsProps extends MappingPropsBase {
  mappings: Mappings;
  variables: ResolveVariables;
}

function resolveMappings({
  mappings,
  submission,
  answers,
  variables,
}: ResolveMappingsProps): unknown {
  if (Array.isArray(mappings)) {
    return resolveMappingsArray({
      mappings,
      submission,
      answers,
      variables,
    });
  } else if (typeof mappings === "string") {
    return resolveExpr({
      expression: mappings,
      submission,
      answers,
      // Erzeuge für jeden Ausdruck ein eigenes variables-Record, damit
      // Änderungen innerhalb von diesem Ausdruck sich nicht auf andere 
      // Ausdrücke auswirken können. Das ist aktuell nur eine flache
      // Kopie, aber das genügt hier, da wir es nur für Flags benutzen,
      // wie z. B. AnteiligerGrundbeitrag.
      variables: {... variables},
    });
  } else if (typeof mappings === "object") {
    return resolveMappingsObject({
      mappings,
      submission,
      answers,
      variables,
    });
  }

  throw new UnexpectedValueError(mappings);
}


export interface MapAnswersToJsonProps extends MappingPropsBase {
  mappings: MappingsObject;
  variables: ResolveVariables;
}

export function mapAnswersToJson({ mappings, answers, submission, variables }: MapAnswersToJsonProps) {
  return resolveMappingsObject({ mappings, answers, submission, variables });
}

